import { Navigate, Outlet, useLocation } from 'react-router';

import * as paths from '@/router/paths';
import {
  getLoginOnlySearch,
  getRedirectAfterLogin,
  isLoginPath,
  saveLoginRedirect,
  withTenantPrefix,
} from '@/router/redirect-from-state';
import { getTenantSlug } from '@/router/tenant-routing';

import { useAuth } from './_hooks/use-auth';

/**
 * Component for checking if the current user is authenticated
 * @returns The child component if the user is authenticated, otherwise redirects to the login page
 */
export const AuthRequired = () => {
  const auth = useAuth();
  const location = useLocation();

  // Show loading state to prevent any content from rendering during auth check
  if (auth.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Redirect to login and preserve full routing intent (path + query) so we can
  // send the user back after login. Stored in both location.state (in-app) and
  // sessionStorage (survives magic link / OAuth full page load). Login URL keeps
  // current search params so partner_id etc. are available for branding.
  if (!auth.user || !auth.user.id) {
    const pathname = location.pathname;
    const search = location.search ?? '';
    if (!isLoginPath(pathname)) {
      saveLoginRedirect(pathname, search);
    }
    const loginOnlySearch = getLoginOnlySearch(search);
    const loginTo =
      paths.LOGIN + (loginOnlySearch ? '?' + loginOnlySearch : '');
    return (
      <Navigate
        to={loginTo}
        state={{
          from: { pathname, search },
        }}
        replace
      />
    );
  }

  // After magic link or SSO the backend sends the user to / (or /login-success).
  // Consume stored redirect so we send them to their intended deep link; only
  // runs when sessionStorage was set by the earlier redirect-to-login above.
  // The redirect is normalised to include the active tenant prefix when the
  // saved path predates the new URL shape, so users land on the correct
  // tenant URL directly instead of bouncing through `LegacyTenantRedirect`.
  const redirectTo = getRedirectAfterLogin(location);
  if (redirectTo) {
    const tenantSlug = getTenantSlug(
      auth.user.saas_tenant_id,
      auth.user.saas_tenants
    );
    const normalised = withTenantPrefix(redirectTo, tenantSlug);
    return <Navigate to={normalised} replace />;
  }

  return <Outlet />;
};
